import { Alert } from "@mui/material";
import { FunctionComponent, useState } from "react";

import { Modal } from "@/components";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { useUpdateListPersonFromTrackerPersonMutation } from "@/pages/Private/redux/list/list.api";

import { TrackerPerson } from "../../../ProfileTracker/schema/trackerPerson";

/**
 * TrackerPersonAvatar component for displaying a single person avatar with tooltip
 */
export const TrackerPersonAvatar: FunctionComponent<{
	person: TrackerPerson;
}> = ({ person }) => {
	const firstName = person.prospect?.firstName || person.name?.split(" ")[0] || "";
	const lastName = person.prospect?.lastName || person.name?.split(" ")[1] || "";
	const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
	const imageUrl = person.prospect?.image;

	const [imageError, setImageError] = useState(false);

	const fullName = person.prospect?.name || person.name || "Unknown";
	const title = person.prospect?.title || "";

	const tooltipContent = (
		<div className="flex flex-col gap-1">
			<span className="font-semibold">{fullName}</span>
			{title && <span className="text-xs text-text-meta">{title}</span>}
		</div>
	);

	return (
		<Tooltip content={tooltipContent}>
			<div className="relative">
				{imageUrl && !imageError ? (
					<img
						alt={fullName}
						className="w-8 h-8 rounded-full border-2 border-white object-cover"
						referrerPolicy="no-referrer"
						src={imageUrl}
						onError={() => setImageError(true)}
					/>
				) : (
					<div className="flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-medium border-2 border-white bg-blue-action">
						{initials || "?"}
					</div>
				)}
			</div>
		</Tooltip>
	);
};

/**
 * TrackerPersonRow component for displaying a single person in the selection modal
 */
export const TrackerPersonRow: FunctionComponent<{
	person: TrackerPerson;
	isSelected: boolean;
	onSelect: () => void;
}> = ({ person, isSelected, onSelect }) => {
	const [imageError, setImageError] = useState(false);

	const firstName = person.prospect?.firstName || person.name?.split(" ")[0] || "";
	const lastName = person.prospect?.lastName || person.name?.split(" ")[1] || "";
	const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
	const imageUrl = person.prospect?.image;
	const fullName = person.prospect?.name || person.name || "Unknown";
	const title = person.prospect?.title || "";
	const email = person.prospect?.email || "";

	return (
		<div
			className={classNames(
				"flex items-center gap-3 p-3 rounded-lg cursor-pointer border-2 transition-all",
				isSelected
					? "border-blue-action bg-blue-50"
					: "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
			)}
			onClick={(e) => {
				e.stopPropagation();
				onSelect();
			}}
		>
			<div className="flex-shrink-0">
				{imageUrl && !imageError ? (
					<img
						alt={fullName}
						className="w-10 h-10 rounded-full object-cover"
						referrerPolicy="no-referrer"
						src={imageUrl}
						onError={() => setImageError(true)}
					/>
				) : (
					<div className="flex items-center justify-center w-10 h-10 rounded-full text-white text-sm font-medium bg-blue-action">
						{initials || "?"}
					</div>
				)}
			</div>
			<div className="flex flex-col flex-1 min-w-0">
				<span className="font-semibold text-sm truncate">{fullName}</span>
				{title && <span className="text-xs text-text-meta truncate">{title}</span>}
				{email && <span className="text-xs text-text-meta truncate">{email}</span>}
			</div>
			<div className="flex-shrink-0">
				<div
					className={classNames(
						"w-5 h-5 rounded-full border-2 flex items-center justify-center",
						isSelected ? "border-blue-action bg-blue-action" : "border-gray-300"
					)}
				>
					{isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
				</div>
			</div>
		</div>
	);
};

interface TrackerPersonSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	trackerPersons: TrackerPerson[];
	listPersonId: number;
	currentProspectId?: number | null;
}

/**
 * TrackerPersonSelectionModal component for selecting a tracker person
 * Allows users to select a person from a list and either replace the current prospect or add as a new entry
 */
export const TrackerPersonSelectionModal: FunctionComponent<TrackerPersonSelectionModalProps> = ({
	isOpen,
	onClose,
	trackerPersons,
	listPersonId,
	currentProspectId,
}) => {
	const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
	const [replaceMode, setReplaceMode] = useState(true);

	const [updateListPerson, { isLoading, error, reset }] =
		useUpdateListPersonFromTrackerPersonMutation();

	// Filter out tracker persons whose prospect is already the current prospect
	const availablePersons = trackerPersons.filter(
		(person) => person.prospectId !== currentProspectId
	);

	const handleConfirm = async () => {
		if (!selectedPersonId) {
			return;
		}

		const result = await updateListPerson({
			listPersonId,
			trackerPersonId: selectedPersonId,
			mode: replaceMode ? "replace" : "add",
		});

		if ("error" in result) {
			console.error("Failed to update list person:", result.error);

			return;
		}

		onClose();
	};

	const handleClose = () => {
		setSelectedPersonId(null);
		setReplaceMode(true);
		reset();
		onClose();
	};

	return (
		<Modal
			disableSave={!selectedPersonId}
			handleClose={handleClose}
			handleSave={(e) => {
				e.stopPropagation();
				handleConfirm();
			}}
			isLoading={isLoading}
			isOpened={isOpen}
			size="sm"
			submitButtonText="Confirm"
			title="Select Person"
		>
			<div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
				{availablePersons.length === 0 ? (
					<p className="text-sm text-gray-500 text-center py-4">
						No other persons available to select
					</p>
				) : (
					availablePersons.map((person) => (
						<TrackerPersonRow
							key={person.id}
							isSelected={selectedPersonId === person.id}
							person={person}
							onSelect={() => setSelectedPersonId(person.id)}
						/>
					))
				)}
			</div>
			<div className="mt-4 pt-4 border-t border-gray-200">
				<Checkbox
					isChecked={replaceMode}
					onChange={(e) => {
						e.stopPropagation();
						setReplaceMode(!replaceMode);
					}}
				>
					Replace current prospect
				</Checkbox>
				<p className="text-xs text-gray-500 mt-1 ml-7">
					{replaceMode
						? "The selected person will replace the current prospect"
						: "The selected person will be added as a new entry"}
				</p>
			</div>
			{error && (
				<div className="mt-4">
					<Alert
						severity="error"
						sx={{ alignItems: "center", bgcolor: "error.light", fontSize: 14 }}
					>
						{(error as { data?: { message?: string } })?.data?.message ||
							"Failed to update list person"}
					</Alert>
				</div>
			)}
		</Modal>
	);
};

interface TrackerPersonsCellProps {
	trackerPersons?: TrackerPerson[] | null;
	listPersonId: number;
	currentProspectId?: number | null;
}

/**
 * TrackerPersonsCell component for displaying tracker persons in the table
 * Shows up to 5 avatars with a +X indicator for more
 */
export const TrackerPersonsCell: FunctionComponent<TrackerPersonsCellProps> = ({
	trackerPersons,
	listPersonId,
	currentProspectId,
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);

	if (!trackerPersons || trackerPersons.length === 0) {
		return <span className="text-gray-400 text-sm italic">-</span>;
	}

	const maxVisible = 5;
	const visiblePersons = trackerPersons.slice(0, maxVisible);
	const remainingCount = trackerPersons.length - maxVisible;

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsModalOpen(true);
	};

	return (
		<div onClick={(e) => e.stopPropagation()}>
			<div
				className="flex items-center -space-x-2 cursor-pointer hover:opacity-80"
				onClick={handleClick}
			>
				{visiblePersons.map((person) => (
					<TrackerPersonAvatar key={person.id} person={person} />
				))}
				{remainingCount > 0 && (
					<div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-600 text-xs font-medium border-2 border-white z-10">
						+{remainingCount}
					</div>
				)}
			</div>
			<TrackerPersonSelectionModal
				currentProspectId={currentProspectId}
				isOpen={isModalOpen}
				listPersonId={listPersonId}
				trackerPersons={trackerPersons}
				onClose={() => setIsModalOpen(false)}
			/>
		</div>
	);
};
